import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { useUser } from '@/context/userContext';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { getMyVaultPath } from '@/bundle/_OrgAdmin/pages/MyVaultPage/path/path';
import { RecentActivityRowType, TaskScopeType, TaskViewType } from '@/types/dashboardTypes';
import { DASHBOARD_QUERY_KEYS, loadRecentActivity, loadSummary, loadTasks } from './api/api';
import { SummaryCards } from './ui/SummaryCards';
import { SetupVaultBanner } from './ui/SetupVaultBanner';
import { TasksTable } from './ui/TasksTable';
import { RecentActivity } from './ui/RecentActivity';
import { NextStepModal } from './ui/NextStepModal/NextStepModal';
import { TaskModal } from './ui/TaskModal/TaskModal';
import { Box } from '@/components/Box/Box';

type ParamsType = { task?: string; scope?: TaskScopeType; view?: TaskViewType };

export const DashboardPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useGetSearchParams<ParamsType>();
  const { replaceSearchParams } = useReplaceSearchParams<ParamsType>();
  const { user } = useUser();
  const { isInactiveSubscription } = useSubscriptionStatus();
  const [nextStepFor, setNextStepFor] = useState<RecentActivityRowType | null>(null);

  const scope: TaskScopeType = searchParams.scope || 'assigned_by_me';
  const view: TaskViewType = searchParams.view || 'open';
  const openTaskId = (searchParams.task as string) || null; // /dashboard?task=<id> from the bell and from emails

  const { data: summaryResponse } = useQuery({ queryKey: DASHBOARD_QUERY_KEYS.summary, queryFn: loadSummary });
  const { data: recentResponse, isPending: isRecentPending } = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.recent,
    queryFn: loadRecentActivity,
  });
  const taskParams = { ...searchParams, scope, view };
  const { data: tasksResponse, isPending: isTasksPending } = useQuery({
    queryKey: [DASHBOARD_QUERY_KEYS.tasks, taskParams],
    queryFn: () => loadTasks(taskParams),
    placeholderData: keepPreviousData,
  });

  const summary = summaryResponse?.body;
  const recent = recentResponse?.body?.results ?? [];
  const tasks = tasksResponse?.body?.results ?? [];

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.tasks] });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.summary });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.notifications });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.unread });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.recent });
  };

  const openTask = (id: string) => replaceSearchParams({ task: id });
  const closeTask = () => {
    replaceSearchParams({ task: null });
    refreshAll();
  };

  useEffect(() => {
    // the assignee lands here from the email: show their tab when they have nothing assigned by them
    if (!searchParams.scope && summary && summary.tasks.open === 0 && summary.tasks.assigned_to_me_open > 0) {
      replaceSearchParams({ scope: 'assigned_to_me' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.tasks.open, summary?.tasks.assigned_to_me_open]);

  return (
    <ContentLayout
      title='Dashboard'
      breadcrumbs='Dashboard'
      subtitle='Verified instructions, insured transfers and ACH requests in one place.'
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        {summary && summary.my_vault.accounts === 0 && <SetupVaultBanner onSetup={() => navigate(getMyVaultPath())} />}

        <Box display='flex' gap='24px' flexDirection='column'>
          <SummaryCards summary={summary} />

          <TasksTable
            tasks={tasks}
            isLoading={isTasksPending}
            scope={scope}
            view={view}
            counts={summary?.tasks}
            onScope={(value) => replaceSearchParams({ scope: value, view: 'open', page: null })}
            onView={(value) => replaceSearchParams({ view: value, page: null })}
            onOpen={openTask}
          />

          <RecentActivity rows={recent} isLoading={isRecentPending} onNextStep={setNextStepFor} />
        </Box>
      </PageBody>

      {nextStepFor && (
        <NextStepModal
          isOpen
          source={nextStepFor}
          onClose={() => setNextStepFor(null)}
          onCreated={(task) => {
            setNextStepFor(null);
            refreshAll();
            openTask(task.id);
          }}
        />
      )}

      {openTaskId && (
        <TaskModal isOpen taskId={openTaskId} currentUserId={user?.id} onClose={closeTask} onChanged={refreshAll} />
      )}
    </ContentLayout>
  );
};
